/**
 * Typed search params for the `/jobs` route. The status filter and the show-archived toggle live
 * in the URL so they are deep-linkable AND compile-checked (the estimates/inventory `searchParams`
 * pattern). TanStack Router validates the raw URL params through `validateJobsSearch` on every
 * navigation.
 *
 * `?status=Active|Completed|On Hold|Cancelled` narrows the list to one status (each status pill
 * NAVIGATES). `?show_archived=1` reveals archived jobs. The coarse endpoint owns the filtering
 * + the status counts; the SPA only carries the flags and NEVER sends a tenant id (SEC-11).
 */

import { ALLOWED_JOB_STATUSES, type JobStatus } from './contractsMirror.js';

export interface JobsSearch {
  status?: JobStatus;
  show_archived?: boolean;
}

/** Coerce a raw URL value into a boolean (`1`/`true` → true). */
function asBool(value: unknown): boolean {
  return value === true || value === '1' || value === 'true';
}

/** Coerce a raw URL value into a known job status, or undefined. */
function asStatus(value: unknown): JobStatus | undefined {
  return ALLOWED_JOB_STATUSES.find((s) => s === value);
}

/**
 * TanStack Router `validateSearch` for the jobs list route. Only keeps each filter when
 * meaningful so the canonical URL stays clean (`/jobs` vs `/jobs?status=Active`).
 */
export function validateJobsSearch(raw: Record<string, unknown>): JobsSearch {
  const search: JobsSearch = {};
  const status = asStatus(raw.status);
  if (status) search.status = status;
  if (asBool(raw.show_archived)) search.show_archived = true;
  return search;
}
